import {computed} from 'vue';
import {NavbarMode, type ModuleRuntimeConfig, type ModeConfig} from '@navbar-unlogged-types';
import {useLocalePrefix, useRuntimeConfig, useState} from '#imports';
import {readonlyState} from '@malt/utils';
import {resolveNavbarModeFromPath} from '../utils/resolveNavbarModeFromPath';

export function useNavbarMode() {
    const runtimeConfig = useRuntimeConfig().public['@malt/nuxt-navbar-unlogged-module'];
    const moduleConfig = useState<ModuleRuntimeConfig>('navbar-unlogged-module-config', () => ({
        modes: (runtimeConfig.modes as ModeConfig[]) ?? [],
        defaultMode: runtimeConfig.defaultMode as NavbarMode,
    }));

    const selectedMode = useState<NavbarMode | undefined>('navbar-unlogged-selected-mode', () => moduleConfig.value.defaultMode);
    const {withoutLocalePrefix} = useLocalePrefix();

    const currentModeConfig = computed((): ModeConfig | undefined => moduleConfig.value.modes!.find((mode) => mode.id === selectedMode.value));

    function switchMode(newMode: NavbarMode) {
        selectedMode.value = newMode;
    }

    function applyNavbarMode(path: string, navbarMeta?: unknown) {
        const routeMode = navbarModeFromMeta(navbarMeta);
        if (routeMode) {
            selectedMode.value = routeMode;
            return;
        }

        const barePath = withoutLocalePrefix(path);
        selectedMode.value = resolveNavbarModeFromPath(barePath, moduleConfig.value.defaultMode ?? NavbarMode.CLIENT);
    }

    const hasMultipleModes = computed(() => moduleConfig.value.modes && moduleConfig.value.modes.length > 1);

    return {
        // consts a-z
        config: readonlyState(moduleConfig),
        currentModeConfig,
        hasMultipleModes,
        selectedMode: readonlyState(selectedMode),
        // functions a-z
        applyNavbarMode,
        switchMode,
    };
}

function navbarModeFromMeta(navbar: unknown): NavbarMode | undefined {
    if (typeof navbar !== 'string') {
        return undefined;
    }

    return configuredNavbarMode(navbar, Object.values(NavbarMode));
}

function configuredNavbarMode(mode: string | null, configuredModes: readonly NavbarMode[]): NavbarMode | undefined {
    if (mode !== null && isNavbarMode(mode) && configuredModes.includes(mode)) {
        return mode;
    }

    return undefined;
}

function isNavbarMode(mode: string): mode is NavbarMode {
    return Object.values(NavbarMode).includes(mode as NavbarMode);
}
